import React, { useContext, useState, useEffect } from "react";
import produitDataFetcher, {
    OffreAlimentaireAnneesInterface,
    OffreAlimentaireWithMarqueInterface,
    OffreAlimentaireAnneesMarqueInterface,
} from "dataProvider/Produit";
import usePromise from "helpers/usePromise";
import Loader from "ui/Loader";
import BackToId from "ui/Button/BackToId";
import PagerNav from "ui/List/PagerNav";
import { IntlContext } from "context";
import Warnings from "../../Common/Warnings";
import { matomoPush } from "../../../Produit/helpers";

const ListAlimentaireMarque: React.FC<{ activeFilters: any }> = ({ activeFilters }) => {
    const intl = useContext(IntlContext);
    const [downloading, setDownloading] = useState<boolean>(false);
    const activeFiltersJson = JSON.stringify(activeFilters);
    const { isLoading, changePromiseParams, data } = usePromise(
        produitDataFetcher.alimentaireWithMarque,
        activeFilters
    );
    const filterFamily = activeFilters?.filter_famille?.length || 0;

    useEffect(() => {
        changePromiseParams(JSON.parse(activeFiltersJson));
    }, [activeFiltersJson, changePromiseParams]);

    if (isLoading || !data) {
        return <Loader message={intl.formatMessage("generic.loader.loading.label")} />;
    }

    const exportRows = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            event.preventDefault();
            setDownloading(true);
            matomoPush([["trackEvent", "Export outil de visualisation", "Offre alimentaire", JSON.stringify(activeFilters?.matomo), 1]]);
            produitDataFetcher
                .alimentaireExportMarque(activeFilters)
                .then((data: any) => {
                    let file = window.URL.createObjectURL(data.content);
                    let tempLink = document.createElement("a");
                    tempLink.href = file;
                    tempLink.download = data.name;
                    tempLink.click();
                    tempLink.parentNode?.removeChild(tempLink);
                    window.URL.revokeObjectURL(file);
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => setDownloading(false));
        } catch (e) {
            console.error(e);
        }
    };

    const totalResults = (): string => {
        let total = data?.length || 0;

        if (typeof total !== "number") {
            return "";
        }
        if (total === 0) {
            return intl.formatMessage("produit.label.no_result");
        }
        return (
            `${total} ` +
            (total > 1 ? intl.formatMessage("offre_alimentaire.label.results") : intl.formatMessage("offre_alimentaire.label.result"))
        );
    };

    const goToPage = (page: number) => {
        changePromiseParams(
            Object.assign({}, data.active_filters, {
                page,
                order: data.pager.order,
                direction: data.pager.direction,
                per_page: data.pager.per_page,
            })
        );
    };

    if (!data || data.length <= 0) {
        return (
            <>
                <div className="stats-container-top">
                    <span className="results">{totalResults()}</span>
                </div>
                <Warnings warnings={data?.warnings} />
            </>
        );
    }

    const firstRow: OffreAlimentaireWithMarqueInterface = data[0];
    const nbTypeMarque = Object.values(firstRow?.type_marque || []).length;
    const nbAnnees = Object.values(Object.values(firstRow?.type_marque || [])[0]?.annees)?.length;

    let lastSecteur: string | null = null,
        dataFooter: HashMap<HashMap<HashMap<OffreAlimentaireAnneesInterface>>> = {};

    const sortAnnees = (a: OffreAlimentaireAnneesInterface, b: OffreAlimentaireAnneesInterface) => {
        const startYearA = parseInt(a.annee.toString());
        const startYearB = parseInt(b.annee.toString());
        return startYearA - startYearB;
    }

    const setLastSecteur = (secteur: string) => {
        lastSecteur = secteur;
    };

    const exportButton = () => {
        return <div className="container--export">
            <button
                type="button"
                className="cta cta--secondary"
                title={intl.formatMessage("generic.export")}
                disabled={downloading}
                onClick={(e) => exportRows(e)}
            >
                {downloading
                    ? intl.formatMessage("generic.loader.loading.label")
                    : intl.formatMessage("generic.export")}
            </button>
        </div>
    };

    const familyExist = () => {
        if (!data || data.length <= 0 || activeFilters?.filter_famille < 1) {
            return true;
        }

        const familles = data.filter((row: OffreAlimentaireWithMarqueInterface) => {
            return activeFilters?.filter_famille?.includes(String(row.famille_id));
        });

        return familles.length > 0;
    }

    return (
        <>
            {exportButton()}
            <div className="stats-container-top">
                <span className="results">{totalResults()}</span>
            </div>
            <div>
                {!!filterFamily && (intl.formatMessage("produit.view_data.alimentaire.familles_comprises"))}
                <br/>
                {!!filterFamily && (intl.formatMessage("produit.view_data.alimentaire.familles_comprises.sup"))}
                {!familyExist() && <><br/>{intl.formatMessage("produit.view_data.alimentaire.familles_comprises.sans_famille")}</>}
            </div>
            <Warnings warnings={data?.warnings} />

            <table className="stats-table-alimentaire">
                <thead className="row-group">
                    <tr className="header-row">
                        <th className="header-cell" rowSpan={3}>
                            {intl.formatMessage("produit.view_data.alimentaire.table.header.secteur")}
                        </th>
                        <th className="header-cell" rowSpan={3}>
                            {intl.formatMessage("produit.view_data.alimentaire.table.header.famille")}
                        </th>
                        <th className="header-cell" colSpan={nbTypeMarque * nbAnnees}>
                            {intl.formatMessage("produit.view_data.alimentaire.table.header.repartition")}
                        </th>
                        <th className="header-cell" colSpan={nbTypeMarque * nbAnnees}>
                            {intl.formatMessage("produit.view_data.alimentaire.table.header.nombre_ref")}
                        </th>
                    </tr>
                    <tr className="header-row">
                        {data &&
                            Object.values(firstRow.type_marque).map(
                                    (data: OffreAlimentaireAnneesMarqueInterface, k: number) => {
                                    return (
                                        <th className="header-cell" colSpan={nbAnnees} key={`${k}-head-marque-rec`}>
                                            {data.name}
                                        </th>
                                    );
                                }
                            )}
                        {data &&
                            Object.values(firstRow.type_marque).map(
                                (data: OffreAlimentaireAnneesMarqueInterface, k: number) => {
                                    return (
                                        <th className="header-cell" colSpan={nbAnnees} key={`${k}-head-marque-prod`}>
                                            {data.name}
                                        </th>
                                    );
                                }
                            )}
                    </tr>
                    <tr className="header-row">
                        {data &&
                            Object.values(firstRow.type_marque).map(
                                (typeMarque: OffreAlimentaireAnneesMarqueInterface) => {
                                    return Object.values(typeMarque.annees).sort(sortAnnees).map((annee: OffreAlimentaireAnneesInterface, k: number) => {
                                        return (
                                            <th className="header-cell" key={`${k}-head-annee-recap`}>
                                                {annee.annee}
                                            </th>
                                        );
                                    });
                                }
                            )}
                        {data &&
                            Object.values(firstRow.type_marque).map(
                                (typeMarque: OffreAlimentaireAnneesMarqueInterface) => {
                                    return Object.values(typeMarque.annees).sort(sortAnnees).map((annee: OffreAlimentaireAnneesInterface, k: number) => {
                                        return (
                                            <th className="header-cell" key={`${k}-head-annee-prod`}>
                                                {annee.annee}
                                            </th>
                                        );
                                    });
                                }
                            )}
                    </tr>
                </thead>
                <tbody>
                    {data.map(
                        (row: OffreAlimentaireWithMarqueInterface, key: number) => {
                            if (lastSecteur !== row.secteur) {
                                dataFooter = {};
                                Object.values(row.type_marque).map((typeMarque: OffreAlimentaireAnneesMarqueInterface) => {
                                    return Object.values(typeMarque.annees).map((annee: OffreAlimentaireAnneesInterface) => {
                                        if (dataFooter[row.secteur_id] === undefined) {
                                            dataFooter[row.secteur_id] = {};
                                        }
                                        if (dataFooter[row.secteur_id][typeMarque.name] === undefined) {
                                            dataFooter[row.secteur_id][typeMarque.name] = {};
                                        }
                                        if (dataFooter[row.secteur_id][typeMarque.name][annee.annee] === undefined) {
                                            dataFooter[row.secteur_id][typeMarque.name][annee.annee] = {
                                                annee: annee.annee,
                                                produits: 0,
                                                repartition: 0,
                                            };
                                        }
                                        return dataFooter;
                                    }, dataFooter);
                                }, dataFooter);
                            }

                            Object.values(row.type_marque).map((typeMarque: OffreAlimentaireAnneesMarqueInterface) => {
                                return Object.values(typeMarque.annees).map((annee: OffreAlimentaireAnneesInterface) => {
                                    dataFooter[row.secteur_id][typeMarque.name][annee.annee].produits += annee.produits;
                                    dataFooter[row.secteur_id][typeMarque.name][annee.annee].repartition += annee.repartition;
                                    return dataFooter;
                                }, dataFooter);
                            }, dataFooter);

                            return (
                                <React.Fragment key={key}>
                                    <tr className={(!!filterFamily && activeFilters?.filter_famille?.includes("" + row.famille_id)) ? "row-light" : ""}>
                                        <td className="cell">{row.secteur}</td>
                                        <td className="cell">{row.famille}</td>
                                        {Object.values(row.type_marque).map(
                                            (typeMarque: OffreAlimentaireAnneesMarqueInterface) => {
                                                return Object.values(typeMarque.annees).sort(sortAnnees).map(
                                                    (annee: OffreAlimentaireAnneesInterface, k: number) => {
                                                        if (annee.repartition === 0) {
                                                            return <td className="header-cell" key={`${k}-recap`}>_</td>
                                                        }
                                                        return (
                                                            <td className="header-cell" key={`${k}-recap`}>
                                                                {intl.formatNumber(annee.repartition, {
                                                                    maximumFractionDigits: 2,
                                                                    minimumFractionDigits: 2,
                                                                    style: "percent",
                                                                })}
                                                            </td>
                                                        );
                                                    }
                                                );
                                            }
                                        )}
                                        {Object.values(row.type_marque).map(
                                            (typeMarque: OffreAlimentaireAnneesMarqueInterface) => {
                                                return Object.values(typeMarque.annees).sort(sortAnnees).map(
                                                    (annee: OffreAlimentaireAnneesInterface, k: number) => {
                                                        if (annee.produits === 0) {
                                                            return <td className="header-cell" key={`${k}-produits`}>_</td>
                                                        }
                                                        return (
                                                            <td className="header-cell" key={`${k}-produit`}>
                                                                {intl.formatNumber(annee.produits, {
                                                                    maximumFractionDigits: 0,
                                                                    minimumFractionDigits: 0,
                                                                })}
                                                            </td>
                                                        );
                                                    }
                                                );
                                            }
                                        )}
                                    </tr>

                                    {row.secteur !== data[key + 1]?.secteur && (
                                        <>
                                            <tr key={`${key}-${row.secteur_id}`} className="tfoot">
                                                <td className="cell">{row.secteur}</td>
                                                <td className="cell">
                                                    {intl.formatMessage(
                                                        "produit.view_data.alimentaire.table.header.familles"
                                                    )}
                                                </td>
                                                {Object.values(dataFooter[row.secteur_id]).map((typeMarque: HashMap<OffreAlimentaireAnneesInterface>) => {
                                                    return Object.values(typeMarque).sort(sortAnnees).map((annee: OffreAlimentaireAnneesInterface, k: number) => {
                                                        if (annee.produits === 0) {
                                                            return <td className="header-cell" key={`${k}-head-produits`}>_</td>
                                                        }
                                                        return (
                                                            <td className="header-cell" key={`${k}-head-reacp`}>
                                                                100 %
                                                            </td>
                                                        );
                                                    });
                                                })}
                                                {Object.values(dataFooter[row.secteur_id]).map((typeMarque: HashMap<OffreAlimentaireAnneesInterface>) => {
                                                    return Object.values(typeMarque).sort(sortAnnees).map((annee: OffreAlimentaireAnneesInterface, k: number) => {
                                                        if (annee.produits === 0) {
                                                            return <td className="header-cell" key={`${k}-head-produits`}>_</td>
                                                        }
                                                        return (
                                                            <td className="header-cell" key={`${k}-head-produits`}>
                                                                {intl.formatNumber(annee.produits, {
                                                                    maximumFractionDigits: 0,
                                                                    minimumFractionDigits: 0,
                                                                })}
                                                            </td>
                                                        );
                                                    });
                                                })}
                                            </tr>
                                        </>
                                    )}
                                    {setLastSecteur(row.secteur)}
                                </React.Fragment>
                            );
                        },
                        { lastSecteur, dataFooter }
                    )}
                </tbody>
            </table>
            {/* -- pagination */}
            <PagerNav
                currentPage={data?.pager?.current_page}
                lastPage={data?.pager?.last_page}
                onChange={goToPage}
            />
            {/* </div> */}
            {exportButton()}

            <BackToId label={intl.formatMessage("generic.go_to.top")} toId="pageup"/>
        </>
    );
};

export default ListAlimentaireMarque;
